import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box, Button, Card, CardHeader, CardContent, Collapse, Typography,
    Grid, FormControl, FormGroup, FormLabel, OutlinedInput,
    Select, MenuItem
} from '@material-ui/core'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { sendEeProviderAlert } from 'config/helpers/integrations-lib'

const useStyles = makeStyles(componentStyles)

function AdminDeliverySettingsCard({ companyDetails, updateCompany }) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new AdminAPI()

    const deliveryDetails = companyDetails.deliveryDetails || {}
    const planDetails = companyDetails.planDetails || {}

    const [deliveryMethod, setDeliveryMethod] = useState(deliveryDetails.deliveryMethod || '')
    const [deliveryEmail, setDeliveryEmail] = useState(deliveryDetails.email || '')
    const [weeklyCap, setWeeklyCap] = useState(planDetails.maxLeads ?? '')
    const [leadPrice, setLeadPrice] = useState(planDetails.leadPrice ?? '')
    const [deliveryStatus, setDeliveryStatus] = useState(planDetails.pauseRequested === false ? 'active' : 'paused')
    const [loading, setLoading] = useState(false)

    // Re-sync when selected provider changes
    useEffect(() => {
        const d = companyDetails.deliveryDetails || {}
        const p = companyDetails.planDetails || {}
        setDeliveryMethod(d.deliveryMethod || '')
        setDeliveryEmail(d.email || '')
        setWeeklyCap(p.maxLeads ?? '')
        setLeadPrice(p.leadPrice ?? '')
        setDeliveryStatus(p.pauseRequested === false ? 'active' : 'paused')
    }, [companyDetails.companyId])

    async function save() {
        setLoading(true)
        // Capture previous state before saving so we can detect first-time set
        const wasDeliveryMethodSet = !!companyDetails.deliveryDetails?.deliveryMethod
        try {
            const capNum = weeklyCap === '' || weeklyCap === null ? null : Number(weeklyCap)
            const priceNum = leadPrice === '' || leadPrice === null ? null : Number(leadPrice)
            const updated = await api.updateDeliverySettings(
                companyDetails.companyId,
                deliveryMethod || null,
                deliveryMethod === 'Email' ? (deliveryEmail || null) : null,
                capNum,
                priceNum,
                deliveryStatus
            )
            if (updated && updated.success === false) {
                NotificationManager.error(updated.message || 'Failed to update delivery settings')
            } else {
                updateCompany(updated)
                NotificationManager.success('Delivery settings saved')
                // Alert: fire once — first time delivery method is set
                if (!wasDeliveryMethodSet && deliveryMethod) {
                    sendEeProviderAlert({
                        type:           'delivery_method_selected',
                        company:        updated,
                        deliveryMethod,
                        weeklyCap:      capNum,
                    })
                }
            }
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box>
                        <Box component={Typography} variant='h2' marginBottom='0!important'>
                            <Box component='span'>Delivery Settings - {companyDetails.name}</Box>
                        </Box>
                        <Box
                            component='p'
                            fontSize='.875rem'
                            marginBottom='0'
                            marginTop='0'
                            lineHeight='1.7'
                            fontWeight='300'
                        >
                            Admin-authoritative delivery configuration. Changes take effect from next week once bd-deliveries is updated manually. A Slack notification is sent on save.
                        </Box>
                    </Box>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            />
            <Collapse in={open}>
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Delivery Method</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <Select
                                    value={deliveryMethod || ''}
                                    onChange={(e) => setDeliveryMethod(e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{ marginBottom: '0.5rem' }}
                                    displayEmpty
                                >
                                    <MenuItem value=''>Not set</MenuItem>
                                    <MenuItem value='Email'>Email</MenuItem>
                                    <MenuItem value='CRM'>CRM</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    {deliveryMethod === 'Email' && (
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Delivery Email(s)</FormLabel>
                                <FormControl variant='filled' component={Box} width='100%'>
                                    <OutlinedInput
                                        type='text'
                                        inputProps={{ multiple: true }}
                                        autoComplete='off'
                                        placeholder='leads@provider.ie, sales@provider.ie'
                                        value={deliveryEmail}
                                        onChange={(e) => setDeliveryEmail(e.target.value)}
                                    />
                                </FormControl>
                                <Typography variant='caption' color='textSecondary'>
                                    Separate multiple addresses with commas to deliver leads to more than one inbox.
                                </Typography>
                            </FormGroup>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Weekly Cap (leads)</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <OutlinedInput
                                    type='number'
                                    inputProps={{ min: 0, step: 1 }}
                                    autoComplete='off'
                                    placeholder='e.g. 20'
                                    value={weeklyCap}
                                    onChange={(e) => setWeeklyCap(e.target.value)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Price Per Lead (€)</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <OutlinedInput
                                    type='number'
                                    inputProps={{ min: 0, step: 0.01 }}
                                    autoComplete='off'
                                    placeholder='e.g. 50'
                                    value={leadPrice}
                                    onChange={(e) => setLeadPrice(e.target.value)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Delivery Status</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <Select
                                    value={deliveryStatus}
                                    onChange={(e) => setDeliveryStatus(e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{ marginBottom: '0.5rem' }}
                                >
                                    <MenuItem value='active'>Active</MenuItem>
                                    <MenuItem value='paused'>Paused</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='flex-end'>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading}
                                onClick={save}
                            >
                                {!loading ? 'Save Delivery Settings' : <Loader height={12} />}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>
        </Card>
    )
}

export default AdminDeliverySettingsCard
